/**
 * Shared helpers for the V2 section/question conditional-visibility controls.
 *
 * Both SectionTriggers (section-level `visibleWhen`) and QuestionVisibilityControls
 * (per-question `visibleWhen`) build the same "eligible source question" dropdown
 * and convert between the RHF `visibleWhen` value and a flat editable Draft. These
 * helpers were previously duplicated verbatim in both files; this module is the
 * single source of truth.
 *
 * NOTE: V1's `ConditionalVisibilityControls` is intentionally NOT a consumer — it
 * operates on a flat `IFormField` model rather than `SurveyPart`, so its
 * same-named helpers are a separate concern.
 */
import type {
	CheckBoxesQuestion,
	DropDownQuestion,
	QuestionVisibility,
	QuestionVisibilityLogical,
	QuestionVisibilitySimple,
	SurveyPart,
	SurveySection,
} from '../../types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A question whose answers can drive section/question visibility. */
export type EligibleSource = DropDownQuestion | CheckBoxesQuestion;

/**
 * A source-question entry with positional context, used to display a
 * "Section X · Question Y" label in the dropdown instead of falling back to the
 * raw fieldId UUID when the question has no human label yet.
 */
export interface SourceEntry {
	question: EligibleSource;
	/** Section index of this source question; -1 for flat (non-sectioned) parts. */
	sectionIndex: number;
	/** Question index within its section; -1 for flat parts. */
	questionIndex: number;
}

/**
 * A group of source entries sharing a section, in the shape Base UI's Combobox
 * expects for grouped `items` (`{ value, items }`). `value` doubles as the
 * group's display label.
 */
export interface SourceGroup {
	/** Display label for the group (also Base UI's group key). */
	value: string;
	items: SourceEntry[];
}

/**
 * Local edit-time mirror of the rule. The BE only accepts a single source
 * `fieldId` per `VisibleWhen` (it rejects with HTTP 400 otherwise), so the rule
 * is shaped as one source + N OR'd answer values.
 */
export interface Draft {
	enabled: boolean;
	dependsOnFieldId?: string;
	dependsOnOptionIds: string[];
}

// ---------------------------------------------------------------------------
// Eligible-source collection, labelling, grouping
// ---------------------------------------------------------------------------

/**
 * Collect the DropDownQuestion / CheckBoxesQuestion eligible to drive the target
 * part's visibility, with positional context for friendly labelling.
 *
 * Always includes every option question in the parts BEFORE `sectionIndex`
 * (descending into SurveySection containers; flat-legacy questions are taken
 * directly).
 *
 * When `questionIndex` is provided (question-level visibility) it ALSO includes
 * the earlier option questions within the target section
 * (`questions[0..questionIndex-1]`). When omitted (section-level visibility) the
 * in-section loop is skipped, so a section's triggers only see questions from
 * prior parts.
 */
export function buildEligibleSources(
	allSurveyParts: SurveyPart[],
	sectionIndex: number,
	questionIndex?: number
): SourceEntry[] {
	const result: SourceEntry[] = [];

	for (let i = 0; i < sectionIndex; i++) {
		const part = allSurveyParts[i];
		if (part?._type === 'SurveySection') {
			const section = part as SurveySection;
			const questions = section.questions ?? [];
			for (let j = 0; j < questions.length; j++) {
				const q = questions[j];
				if (q?._type === 'DropDownQuestion' || q?._type === 'CheckBoxesQuestion') {
					result.push({ question: q as EligibleSource, sectionIndex: i, questionIndex: j });
				}
			}
		} else if (part?._type === 'DropDownQuestion' || part?._type === 'CheckBoxesQuestion') {
			result.push({ question: part as EligibleSource, sectionIndex: -1, questionIndex: -1 });
		}
	}

	if (questionIndex !== undefined) {
		const current = allSurveyParts[sectionIndex];
		if (current?._type === 'SurveySection') {
			const section = current as SurveySection;
			const questions = section.questions ?? [];
			for (let j = 0; j < questionIndex; j++) {
				const q = questions[j];
				if (q?._type === 'DropDownQuestion' || q?._type === 'CheckBoxesQuestion') {
					result.push({ question: q as EligibleSource, sectionIndex, questionIndex: j });
				}
			}
		}
	}

	return result;
}

/**
 * Friendly label for a source entry shown inside the grouped Combobox. The
 * enclosing group label already carries the "Section X" context, so the per-item
 * text only needs the question's own position + label: "Question Y — <label>"
 * (or just "Question Y" when the question has no label yet). Legacy flat
 * questions (sectionIndex === -1) have no question index, so they fall back to
 * the bare label / "Question".
 */
export function describeSource(entry: SourceEntry): string {
	const base = entry.questionIndex >= 0 ? `Question ${entry.questionIndex + 1}` : 'Question';
	const label = entry.question.label?.trim();
	return label ? `${base} — ${label}` : base;
}

/**
 * Group eligible source entries by their owning section, preserving the original
 * order of `eligibleSources`. The group label is "Section {n} — {title}" (or just
 * "Section {n}" when the title is blank). Legacy flat questions (sectionIndex -1)
 * collect under a single "Questions" group.
 */
export function groupEligibleSources(eligibleSources: SourceEntry[], allSurveyParts: SurveyPart[]): SourceGroup[] {
	const groups: SourceGroup[] = [];
	const bySectionIndex = new Map<number, SourceGroup>();

	for (const entry of eligibleSources) {
		let group = bySectionIndex.get(entry.sectionIndex);
		if (!group) {
			let label: string;
			if (entry.sectionIndex < 0) {
				label = 'Questions';
			} else {
				const section = allSurveyParts[entry.sectionIndex] as SurveySection | undefined;
				const title = section?.title?.trim();
				label = title ? `Section ${entry.sectionIndex + 1} — ${title}` : `Section ${entry.sectionIndex + 1}`;
			}
			group = { value: label, items: [] };
			bySectionIndex.set(entry.sectionIndex, group);
			groups.push(group);
		}
		group.items.push(entry);
	}

	return groups;
}

// ---------------------------------------------------------------------------
// Draft <-> visibleWhen conversion
// ---------------------------------------------------------------------------

/**
 * Normalise the RHF `visibleWhen` value into a flat list of editable rows.
 */
export function fromVisibleWhen(visibleWhen: QuestionVisibility | undefined): Draft {
	if (!visibleWhen) {
		return { enabled: false, dependsOnFieldId: undefined, dependsOnOptionIds: [] };
	}
	if (visibleWhen._type === 'SimpleEqualityQuestionVisibility') {
		const simple = visibleWhen as QuestionVisibilitySimple;
		return {
			enabled: true,
			dependsOnFieldId: simple.fieldId,
			dependsOnOptionIds: [simple.fieldValue],
		};
	}
	const logical = visibleWhen as QuestionVisibilityLogical;
	const simples = (logical.visibilities ?? []).filter(
		(v): v is QuestionVisibilitySimple => v._type === 'SimpleEqualityQuestionVisibility'
	);
	return {
		enabled: true,
		dependsOnFieldId: simples[0]?.fieldId,
		dependsOnOptionIds: simples.map(s => s.fieldValue),
	};
}

/**
 * Collapse the editable Draft back into the RHF `visibleWhen` shape:
 *   - 0 answers          → undefined
 *   - 1 answer           → SimpleEqualityQuestionVisibility
 *   - 2+ answers (OR'd)  → LogicalQuestionVisibility { logicalOperator: 'Or' }
 */
export function toVisibleWhen(draft: Draft): QuestionVisibility | undefined {
	if (!draft.enabled || !draft.dependsOnFieldId) return undefined;
	const values = draft.dependsOnOptionIds.filter(v => !!v);
	if (values.length === 0) return undefined;
	if (values.length === 1) {
		return {
			_type: 'SimpleEqualityQuestionVisibility',
			fieldId: draft.dependsOnFieldId,
			fieldValue: values[0],
		} satisfies QuestionVisibilitySimple;
	}
	return {
		_type: 'LogicalQuestionVisibility',
		logicalOperator: 'Or',
		visibilities: values.map(v => ({
			_type: 'SimpleEqualityQuestionVisibility',
			fieldId: draft.dependsOnFieldId!,
			fieldValue: v,
		})),
	} satisfies QuestionVisibilityLogical;
}

/**
 * Set of option ids currently picked by ANY OTHER answer row (excludes the
 * current row's own selection). Used to disable already-used options in each
 * row's dropdown so the same answer can't be added twice. Mirrors V1's
 * `computeUsedByOtherRows` in ConditionalVisibilityControls.
 */
export function computeUsedByOtherRows(dependsOnOptionIds: string[], currentRowIndex: number): Set<string> {
	const result = new Set<string>();
	dependsOnOptionIds.forEach((id, idx) => {
		if (idx !== currentRowIndex && id) result.add(id);
	});
	return result;
}
